// Filas de Comisiones pagadas. El corte en curso se agrega igual que el motor agrega los cerrados
// (`payoutReports.ts`), para que "Esta semana" y los demás periodos se lean igual.
import type { RetailCommission } from '@fiado/type-kit';
import { orEmpty } from './orEmpty';

type Breakdown = RetailCommission.CommissionSellerBreakdown;
type Row = RetailCommission.CommissionAggregateRow;
export type AggregateBy = `${RetailCommission.CommissionAggregateByEnum}`;

export interface RulePaid {
    ruleId: string;
    name: string;
    /** Piezas que pagaron la regla; el umbral y la renta no cuentan piezas. */
    unitsCount: number;
    paidCents: number;
}

/**
 * Lo que cada regla le pagó a un vendedor en el corte, como lo suma el motor por regla: el bono con
 * su parte del multiplicador, el umbral y la renta de cartera, una entrada por regla.
 */
export function sellerPaidByRule(b: Breakdown): RulePaid[] {
    const paid = new Map<string, RulePaid>();
    const add = (ruleId: string, name: string, unitsCount: number, paidCents: number) => {
        const prev = paid.get(ruleId);
        paid.set(ruleId, {
            ruleId,
            name: prev?.name ?? name,
            unitsCount: (prev?.unitsCount ?? 0) + unitsCount,
            paidCents: (prev?.paidCents ?? 0) + paidCents,
        });
    };
    for (const r of orEmpty(b.byRule))
        add(r.ruleId, r.name, r.unitsCount, r.bonusCents + r.multiplierShareCents);
    for (const t of orEmpty(b.thresholds)) add(t.ruleId, t.name, 0, t.amountCents);
    for (const r of b.rentByRule ?? []) add(r.ruleId, r.name, 0, r.amountCents);
    return [...paid.values()];
}

export function aggregateBreakdowns(breakdowns: readonly Breakdown[], by: AggregateBy): Row[] {
    const rows = new Map<string, { row: Row; sellers: Set<string> }>();
    const add = (key: string, name: string | null, sellerId: string, values: Partial<Row>) => {
        const entry = rows.get(key) ?? {
            row: {
                key,
                name,
                sellersCount: 0,
                salesCount: 0,
                unitsCount: 0,
                placementCents: 0,
                paidCents: 0,
            },
            sellers: new Set<string>(),
        };
        entry.sellers.add(sellerId);
        entry.row.salesCount += values.salesCount ?? 0;
        entry.row.unitsCount += values.unitsCount ?? 0;
        entry.row.placementCents += values.placementCents ?? 0;
        entry.row.paidCents += values.paidCents ?? 0;
        rows.set(key, entry);
    };

    for (const b of breakdowns) {
        if (by === 'RULE') {
            for (const r of sellerPaidByRule(b))
                add(r.ruleId, r.name, b.sellerId, {
                    unitsCount: r.unitsCount,
                    paidCents: r.paidCents,
                });
        } else if (by === 'STORE') {
            for (const s of orEmpty(b.byStore)) add(s.storeId, null, b.sellerId, s);
        } else if (by === 'SKU') {
            for (const s of orEmpty(b.bySku)) add(s.sku, null, b.sellerId, s);
        } else {
            add(b.sellerId, null, b.sellerId, {
                salesCount: b.salesCount,
                unitsCount: b.unitsCount,
                placementCents: b.placementCents,
                paidCents: b.netCents,
            });
        }
    }

    return [...rows.values()]
        .map(({ row, sellers }) => ({ ...row, sellersCount: sellers.size }))
        .sort((a, b) => b.paidCents - a.paidCents);
}

/**
 * Los cortes cerrados más el corte en curso, sumados por clave. Las filas no dicen qué vendedores
 * repiten entre los dos, así que `sellersCount` toma el mayor: nunca cuenta a alguien dos veces.
 */
export function mergeAggregateRows(closed: readonly Row[], current: readonly Row[]): Row[] {
    const rows = new Map<string, Row>(closed.map((row) => [row.key, { ...row }]));
    for (const row of current) {
        const prev = rows.get(row.key);
        rows.set(
            row.key,
            prev
                ? {
                      key: row.key,
                      name: prev.name ?? row.name,
                      sellersCount: Math.max(prev.sellersCount, row.sellersCount),
                      salesCount: prev.salesCount + row.salesCount,
                      unitsCount: prev.unitsCount + row.unitsCount,
                      placementCents: prev.placementCents + row.placementCents,
                      paidCents: prev.paidCents + row.paidCents,
                  }
                : { ...row },
        );
    }
    return [...rows.values()].sort((a, b) => b.paidCents - a.paidCents);
}

/** Las claves con vendedores en los dos lados: al mezclarlas, su `sellersCount` es "al menos". */
export function inexactSellerKeys(closed: readonly Row[], current: readonly Row[]): Set<string> {
    const closedKeys = new Set(closed.filter((r) => r.sellersCount > 0).map((r) => r.key));
    return new Set(
        current.filter((r) => r.sellersCount > 0 && closedKeys.has(r.key)).map((r) => r.key),
    );
}

export function sharePct(paidCents: number, totalCents: number): number {
    return totalCents > 0 ? (paidCents / totalCents) * 100 : 0;
}
